<script setup>
import CustomButton from '~/components/UI/CustomButton.vue'
const props = defineProps({
  notification: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['close', 'markAsReaded'])

const { t } = useI18n()

const iconMap = {
  promotion: { icon: 'material-symbols:real-estate-agent', color: 'text-blue-500', bg: 'bg-blue-50' },
  quote: { icon: 'material-symbols:description', color: 'text-orange-500', bg: 'bg-orange-50' },
  message: { icon: 'material-symbols:chat', color: 'text-green-500', bg: 'bg-green-50' },
  invitation: { icon: 'material-symbols:mail', color: 'text-purple-500', bg: 'bg-purple-50' },
  budget: { icon: 'material-symbols:account-balance-wallet', color: 'text-cyan-500', bg: 'bg-cyan-50' },
  company: { icon: 'material-symbols:account-balance', color: 'text-indigo-500', bg: 'bg-indigo-50' },
  system: { icon: 'material-symbols:info', color: 'text-gray-500', bg: 'bg-gray-50' }
}

const iconConfig = computed(() => iconMap[props.notification.type] || iconMap.system)

const formattedDate = computed(() => {
  const dayjs = useDayjs()
  return dayjs(props.notification.createdAt).format('DD/MM/YYYY HH:mm')
})

onMounted(() => {
  if (!props.notification.isReaded) {
    setTimeout(() => {
      emit('markAsReaded', props.notification.id)
    }, 1500)
  }
})

function handleAction() {
  if (props.notification.actionUrl) {
    navigateTo(props.notification.actionUrl)
  }
  emit('close')
}

function handleClose() {
  emit('close')
}
</script>

<template>
  <div class="-m-6 w-full max-w-lg mx-auto overflow-hidden">
    <div class="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
      <h3 class="font-semibold text-slate-800 text-lg">
        {{ t('notifications.detail') }}
      </h3>
    </div>

    <div class="px-6 py-5">
      <div class="flex items-start gap-4">
        <div
          class="flex-shrink-0 size-12 rounded-full flex items-center justify-center"
          :class="iconConfig.bg"
        >
          <Icon :name="iconConfig.icon" class="text-2xl" :class="iconConfig.color" />
        </div>

        <div class="flex-1">
          <h4 class="font-semibold text-slate-800">
            {{ notification.title }}
          </h4>
          <p class="text-xs text-slate-400 mt-1">
            {{ formattedDate }}
          </p>
          <span
            v-if="notification.promotion?.name"
            class="mt-2 inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600"
          >
            <Icon name="material-symbols:real-estate-agent" class="text-xs" />
            {{ notification.promotion.name }}
          </span>
        </div>
      </div>

      <p class="text-sm text-slate-600 mt-4 leading-relaxed">
        {{ notification.message }}
      </p>
    </div>

    <div class="px-6 py-4 border-t border-slate-100 bg-slate-50 flex gap-3">
      <CustomButton
        v-if="notification.actionUrl"
        variant="primary"
        class="flex-1"
        @click="handleAction"
      >
        {{ t('notifications.goTo') }}
      </CustomButton>
      <CustomButton
        variant="secondary"
        class="flex-1"
        @click="handleClose"
      >
        {{ t('common.close') }}
      </CustomButton>
    </div>
  </div>
</template>
